import type { Ref } from 'react';
import * as React from 'react';

import TextArea, { type TextAreaProps } from './TextArea';

type UseAutosizeTextAreaProps = {
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  minHeight: number;
  maxHeight: number;
  triggerAutoSize: string;
};

export const useAutosizeTextArea = ({
  textAreaRef,
  triggerAutoSize,
  maxHeight,
  minHeight,
}: UseAutosizeTextAreaProps) => {
  const [init, setInit] = React.useState(true);

  React.useEffect(() => {
    const el = textAreaRef.current;
    if (!el) return;

    if (init) {
      el.style.minHeight = `${minHeight}px`;
      if (maxHeight > minHeight) {
        el.style.maxHeight = `${maxHeight}px`;
      }
      setInit(false);
    }

    el.style.height = `${minHeight}px`;
    const scrollHeight = el.scrollHeight + 2;

    if (scrollHeight > maxHeight) {
      el.style.height = `${maxHeight}px`;
    } else {
      el.style.height = `${scrollHeight}px`;
    }
  }, [textAreaRef, triggerAutoSize, minHeight, maxHeight, init]);
};

const DEFAULT_MIN_ROWS = 2;

const PIXELS_PER_ROW = {
  small: 24,
  medium: 32,
  large: 40,
};

export type TextAreaAutosizeProps = Omit<TextAreaProps, 'ref'> & {
  ref?: Ref<HTMLTextAreaElement>;
  minHeight?: number;
  maxHeight?: number;
  minRows?: number;
  maxRows?: number;
};

const TextAreaAutosize = React.forwardRef<HTMLTextAreaElement, TextAreaAutosizeProps>(
  (
    {
      size = 'large',
      minHeight,
      maxHeight = Number.MAX_SAFE_INTEGER,
      minRows,
      maxRows,
      onChange,
      value,
      ...props
    },
    ref,
  ) => {
    const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = useMergedRefs(ref, textAreaRef);
    const [triggerAutoSize, setTriggerAutoSize] = React.useState(() =>
      String(value ?? props.defaultValue ?? ''),
    );

    const resolvedMinHeight =
      minHeight ??
      (minRows != null ? minRows * PIXELS_PER_ROW[size] : undefined) ??
      PIXELS_PER_ROW[size] * DEFAULT_MIN_ROWS;
    const resolvedMaxHeight =
      maxHeight !== Number.MAX_SAFE_INTEGER
        ? maxHeight
        : maxRows != null
          ? maxRows * PIXELS_PER_ROW[size]
          : Number.MAX_SAFE_INTEGER;

    useAutosizeTextArea({
      textAreaRef,
      triggerAutoSize,
      minHeight: resolvedMinHeight,
      maxHeight: resolvedMaxHeight,
    });

    React.useEffect(() => {
      setTriggerAutoSize(String(value ?? props.defaultValue ?? ''));
    }, [value, props.defaultValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTriggerAutoSize(e.target.value);
      onChange?.(e);
    };

    return (
      <TextArea ref={mergedRef} size={size} value={value} onChange={handleChange} {...props} />
    );
  },
);

TextAreaAutosize.displayName = 'TextAreaAutosize';

export { TextAreaAutosize };
export default TextAreaAutosize;

function useMergedRefs<T>(...refs: (Ref<T> | undefined)[]): Ref<T> {
  return React.useCallback(
    (node: T) => {
      refs.forEach((r) => {
        if (typeof r === 'function') {
          r(node);
        } else if (r != null) {
          (r as { current: T | null }).current = node;
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs,
  );
}
